import React from "react";
import { useTranslation } from "react-i18next";

const OrganizerRenderPaginationNumbers = ({ currentPage, totalPages, handlePageChange }) => {
  const { t } = useTranslation("organizers");
  const pages = [];
  
  // Helper function to create a page button
  const createPageButton = (pageNumber, isActive = false) => {
    // Dynamic width based on page number length
    const pageNumberLength = pageNumber.toString().length;
    const buttonWidth = pageNumberLength > 2 ? 'min-w-[40px] px-2' : 'w-8';
    
    return (
      <button
        aria-label={t("renderPaginationNumbers.ariaLabel", { page: pageNumber })}
        className={`${buttonWidth} h-8 text-xl flex justify-center items-center mx-[5px] text-[#888888] border border-[#888888] cursor-pointer rounded-full transition-all duration-200 hover:bg-gray-50 ${
          isActive ? "border-1 border-black text-black bg-gray-100" : ""
        }`}
        key={pageNumber}
        onClick={() => handlePageChange(pageNumber)}
      >
        {pageNumber}
      </button>
    );
  };

  // Helper function to create ellipsis
  const createEllipsis = (key) => (
    <span
      key={key}
      className="w-8 h-8 text-xl flex justify-center items-center mx-[5px] text-[#888888]"
    >
      ...
    </span>
  );

  // Smart pagination logic
  if (totalPages <= 1) {
    return pages;
  }

  // Always show first page
  pages.push(createPageButton(1, currentPage === 1));

  if (totalPages <= 5) {
    // If 5 or fewer pages, show all pages
    for (let i = 2; i <= totalPages; i++) {
      pages.push(createPageButton(i, currentPage === i));
    }
  } else {
    // More than 5 pages - smart pagination
    
    // Determine middle pages around current page
    let startMiddle = Math.max(2, currentPage - 1);
    let endMiddle = Math.min(totalPages - 1, currentPage + 1);
    
    // Adjust if current page is near the beginning
    if (currentPage <= 3) {
      startMiddle = 2;
      endMiddle = 3;
    }
    
    // Adjust if current page is near the end
    if (currentPage >= totalPages - 2) {
      startMiddle = totalPages - 2;
      endMiddle = totalPages - 1;
    }

    // Add left ellipsis if there's a gap between first page and middle section
    if (startMiddle > 2) {
      pages.push(createEllipsis("left-ellipsis"));
    }

    // Add middle pages
    for (let i = startMiddle; i <= endMiddle; i++) {
      pages.push(createPageButton(i, currentPage === i));
    }

    // Add right ellipsis if there's a gap between middle section and last page
    if (endMiddle < totalPages - 1) {
      pages.push(createEllipsis("right-ellipsis"));
    }

    // Always show last page (if it's not already shown)
    if (totalPages > 1) {
      pages.push(createPageButton(totalPages, currentPage === totalPages));
    }
  }

  return pages;
};

export default OrganizerRenderPaginationNumbers;